import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Plus, Pencil, Trash2, GitBranch, Settings, HelpCircle, ChevronsUpDown, Check, LayoutList, Kanban as KanbanIcon, User } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { getProgressIndicatorStyle } from '@/lib/progress-utils';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDroppable, closestCenter,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tooltip as UiTooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { projetotService } from '@/services/projetotApi';
import { tipoProjetoService } from '@/services/projetosApi';
import { usuarioService, empresaService } from '@/services/api';
import { ProjetoT, ProjetoTDashboard, ProjetoStatus, TipoProjeto, Usuario, Empresa } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { ProjetoStatusBadge } from '@/components/projetos/ProjetoStatusBadge';
import { useGerenciadorColunas, ColunaResizeHandle, BotaoColunas, type ColunaDef } from '@/components/table/colunas-gerenciaveis';

const STATUS_LABELS: Record<ProjetoStatus, string> = {
  em_edicao: 'Em Edição',
  ag_aprovacao: 'Ag. Aprovação',
  em_andamento: 'Em Andamento',
  ag_validacao: 'Ag. Validação',
  finalizado: 'Finalizado',
  reprovado: 'Reprovado',
};

// Ordem das colunas do Kanban — segue o fluxo de aprovação (em_edicao → ... → finalizado),
// "Reprovado" por último por ser alcançável de qualquer etapa, não parte da sequência normal.
const KANBAN_COLUNAS: ProjetoStatus[] = [
  'em_edicao', 'ag_aprovacao', 'em_andamento', 'ag_validacao', 'finalizado', 'reprovado',
];

function KanbanColuna({ status, children }: { status: ProjetoStatus; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex w-72 shrink-0 flex-col gap-2 rounded-lg border bg-muted/30 p-2 transition-colors',
        isOver && 'bg-primary/5 border-primary/40',
      )}
    >
      {children}
    </div>
  );
}

function KanbanCard({ projeto, onOpen }: { projeto: ProjetoT; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: projeto.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      className={cn(
        'cursor-grab space-y-2 rounded-md border bg-card p-3 text-sm shadow-sm active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
    >
      <p className="font-medium leading-snug">{projeto.titulo}</p>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1 truncate">
          <User className="h-3 w-3 shrink-0" />
          {projeto.lider?.nome || '—'}
        </span>
        {projeto.tipo?.nome && <span className="truncate rounded bg-muted px-1.5 py-0.5">{projeto.tipo.nome}</span>}
      </div>
      <div className="flex items-center gap-2">
        <Progress value={projeto.progresso ?? 0} className={cn("h-1.5 flex-1", getProgressIndicatorStyle(projeto.progresso ?? 0))} />
        <span className="text-[11px] text-muted-foreground">{projeto.progresso ?? 0}%</span>
      </div>
    </div>
  );
}

const PIE_COLORS = ['#6b7280', '#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444'];

const EMPTY_FORM = {
  titulo: '', area: '', descricao: '', lider_id: '', tipo_id: '', data_inicio: '', data_fim: '', is_modelo: false,
};

type InvestimentoLocal = { item: string; valor: number; quantidade: number };
const EMPTY_INVEST: InvestimentoLocal = { item: '', valor: 0, quantidade: 1 };

const formatBRL = (valor: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

function fmtDate(val?: string | null) {
  if (!val) return '—';
  return new Date(val + 'T00:00:00').toLocaleDateString('pt-BR');
}

// ─── Colunas da tabela de Lista de Projetos (largura/ordem/visibilidade, por navegador) ────

type ColunaListaId = 'titulo' | 'area' | 'tipo' | 'lider' | 'status' | 'progresso' | 'data_inicio' | 'data_fim';

const COLUNAS_LISTA_PROJETOS: ColunaDef<ColunaListaId>[] = [
  { id: 'titulo', label: 'Título', defaultWidth: 220, minWidth: 140, defaultVisible: true, travada: true },
  { id: 'tipo', label: 'Tipo', defaultWidth: 140, minWidth: 90, defaultVisible: true },
  { id: 'area', label: 'Área', defaultWidth: 130, minWidth: 90, defaultVisible: true },
  { id: 'lider', label: 'Líder', defaultWidth: 140, minWidth: 90, defaultVisible: true },
  { id: 'status', label: 'Status', defaultWidth: 150, minWidth: 110, defaultVisible: true },
  { id: 'progresso', label: 'Progresso', defaultWidth: 160, minWidth: 120, defaultVisible: true },
  { id: 'data_inicio', label: 'Início', defaultWidth: 100, minWidth: 80, defaultVisible: true },
  { id: 'data_fim', label: 'Fim', defaultWidth: 100, minWidth: 80, defaultVisible: true },
];

// Célula de cada coluna gerenciável — mesmo conteúdo/estilo de antes, só endereçável por id.
function celulaProjeto(colId: ColunaListaId, p: ProjetoT): { className: string; content: ReactNode } {
  switch (colId) {
    case 'titulo':
      return { className: 'font-medium truncate', content: p.titulo };
    case 'area':
      return { className: 'truncate', content: p.area };
    case 'tipo':
      return { className: 'truncate', content: p.tipo?.nome || '—' };
    case 'lider':
      return { className: 'truncate', content: p.lider?.nome || '—' };
    case 'status':
      return { className: '', content: <ProjetoStatusBadge status={p.status_projeto} /> };
    case 'progresso':
      return {
        className: '',
        content: (
          <div className="flex items-center gap-2">
            <Progress value={p.progresso ?? 0} className={cn('h-1.5 w-20', getProgressIndicatorStyle(p.progresso ?? 0))} />
            <span className="text-xs text-muted-foreground">{p.progresso ?? 0}%</span>
          </div>
        ),
      };
    case 'data_inicio':
      return { className: 'text-sm', content: fmtDate(p.data_inicio) };
    case 'data_fim':
      return { className: 'text-sm', content: fmtDate(p.data_fim) };
  }
}

function LabelComAjuda({ texto, ajuda }: { texto: string; ajuda: string }) {
  return (
    <div className="flex items-center gap-1">
      <Label>{texto}</Label>
      <UiTooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-muted-foreground hover:text-foreground" tabIndex={-1}>
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" align="start" className="max-w-64">{ajuda}</TooltipContent>
      </UiTooltip>
    </div>
  );
}

export default function ProjetoTPage() {
  const navigate = useNavigate();
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const isSuperAdmin = user?.tipo === 'superadmin';
  // Usuário comum cria projeto e edita os que lidera ou criou (decisão de 2026-09-23); excluir,
  // marcar como modelo, cadastrar tipo e aprovar continuam só com admin.
  const podeEditar = (p: ProjetoT) => isAdmin || (!!user && (p.lider_id === user.id || p.criado_por === user.id));
  // Segue o mesmo padrão do resto do app: filtro de empresa é global (cabeçalho), lido aqui
  // como valor derivado — nunca um useState local próprio (ver bug corrigido em Projetos/Cronogramas).
  const filtroEmpresa = empresaFiltroId;

  const [projetos, setProjetos] = useState<ProjetoT[]>([]);
  const [dashboard, setDashboard] = useState<ProjetoTDashboard | null>(null);
  const [tipos, setTipos] = useState<TipoProjeto[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<ProjetoStatus | 'todos'>('todos');
  const [buscaTitulo, setBuscaTitulo] = useState('');
  const [filtroArea, setFiltroArea] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [aba, setAba] = useState<'dashboard' | 'projetos' | 'configuracoes'>('projetos');
  const [vista, setVista] = useState<'lista' | 'kanban'>('lista');
  const colGerenciador = useGerenciadorColunas(COLUNAS_LISTA_PROJETOS, 'projetot-colunas-lista-v2');
  const [kanbanDragId, setKanbanDragId] = useState<string | null>(null);
  const kanbanSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<ProjetoT | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [possuiInvestimentos, setPossuiInvestimentos] = useState(false);
  const [investimentos, setInvestimentos] = useState<InvestimentoLocal[]>([]);
  const [saving, setSaving] = useState(false);
  const [paraExcluir, setParaExcluir] = useState<ProjetoT | null>(null);
  const [liderPopoverOpen, setLiderPopoverOpen] = useState(false);
  const [liderSearch, setLiderSearch] = useState('');
  // Vazio = criar do zero. Preenchido = "Criar a partir de modelo" (POST /projetot/:id/criar-a-partir),
  // que copia seções/atividades/subtarefas do projeto marcado como is_modelo.
  const [modeloOrigemId, setModeloOrigemId] = useState('');

  // ── Tipos de Projeto (Configurações) ─────────────────────────────────────
  const [tipoModalOpen, setTipoModalOpen] = useState(false);
  const [tipoEditando, setTipoEditando] = useState<TipoProjeto | null>(null);
  const [tipoNome, setTipoNome] = useState('');
  const [tipoDescricao, setTipoDescricao] = useState('');
  const [tipoLoading, setTipoLoading] = useState(false);
  const [tipoDeleteOpen, setTipoDeleteOpen] = useState(false);
  const [tipoDeletando, setTipoDeletando] = useState<TipoProjeto | null>(null);
  // true quando "Novo Tipo" foi aberto a partir do "+ Novo tipo" do select do form de ProjetoT —
  // faz o tipo recém-criado ser selecionado automaticamente de volta no form, sem precisar
  // procurar na lista de novo.
  const [novoTipoParaForm, setNovoTipoParaForm] = useState(false);

  const totalInvestimentos = investimentos.reduce((acc, i) => acc + i.valor * i.quantidade, 0);
  const addInvestimento = () => setInvestimentos((prev) => [...prev, { ...EMPTY_INVEST }]);
  const updateInvestimento = (idx: number, field: keyof InvestimentoLocal, value: string | number) => {
    setInvestimentos((prev) => prev.map((inv, i) => (i === idx ? { ...inv, [field]: value } : inv)));
  };
  const removeInvestimento = (idx: number) => setInvestimentos((prev) => prev.filter((_, i) => i !== idx));

  const irParaFiltro = (status: ProjetoStatus | 'todos') => {
    setFiltroStatus(status);
    setAba('projetos');
  };

  const load = async () => {
    // ProjetoT exige uma empresa específica (não suporta "Todas as empresas") — mesmo padrão de
    // Organograma/OKRs/Departamentos/Fluxograma. Sem empresa escolhida, nem tenta buscar.
    if (isSuperAdmin && filtroEmpresa === null) {
      setProjetos([]);
      setDashboard(null);
      setTipos([]);
      setLoading(false);
      if (empresas.length === 0) {
        const eRes = await empresaService.getAll();
        if (eRes.success) setEmpresas(eRes.data);
      }
      return;
    }
    setLoading(true);
    const empresaId = isSuperAdmin ? (filtroEmpresa ?? undefined) : undefined;
    const promises = [
      projetotService.getAll(undefined, empresaId),
      projetotService.getDashboard(empresaId),
      tipoProjetoService.getAll(empresaId),
      usuarioService.getAll(),
    ] as const;
    const [pRes, dRes, tRes, uRes] = await Promise.all(promises);
    if (pRes.success) setProjetos(pRes.data);
    else toast.error(pRes.error || 'Erro ao carregar projetos');
    if (dRes.success) setDashboard(dRes.data);
    if (tRes.success) setTipos(tRes.data.filter(t => t.ativo));
    if (uRes.success) setUsuarios(uRes.data);
    if (isSuperAdmin) {
      const eRes = await empresaService.getAll();
      if (eRes.success) setEmpresas(eRes.data);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [empresaAtiva?.id, filtroEmpresa]);

  const openCreate = () => {
    setEditando(null);
    setForm(EMPTY_FORM);
    setModeloOrigemId('');
    setPossuiInvestimentos(false);
    setInvestimentos([]);
    setModalOpen(true);
  };

  const openEdit = async (p: ProjetoT) => {
    setEditando(p);
    setModeloOrigemId('');
    setForm({
      titulo: p.titulo,
      area: p.area,
      descricao: p.descricao || '',
      lider_id: p.lider_id || '',
      tipo_id: p.tipo_id || '',
      data_inicio: p.data_inicio || '',
      data_fim: p.data_fim || '',
      is_modelo: p.is_modelo ?? false,
    });
    setPossuiInvestimentos(false);
    setInvestimentos([]);
    setModalOpen(true);
    // a listagem não traz investimentos — busca o registro completo pra popular o formulário
    const res = await projetotService.getById(p.id);
    if (res.success) {
      const invs = res.data.investimentos || [];
      setPossuiInvestimentos(invs.length > 0);
      setInvestimentos(invs.map((i) => ({ item: i.item, valor: Number(i.valor), quantidade: i.quantidade })));
    }
  };

  const handleSave = async () => {
    // `descricao` é o objetivo unificado — obrigatório no formulário, ainda que a coluna
    // aceite nulo no banco por causa dos registros de Cronograma pré-existentes. `area` é opcional.
    if (!form.titulo || !form.descricao || !form.lider_id || !form.tipo_id) {
      toast.error('Título, objetivo, líder e tipo são obrigatórios');
      return;
    }
    if (!editando && isSuperAdmin && filtroEmpresa === null) {
      toast.error('Selecione uma empresa no filtro do topo antes de criar um projeto');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      investimentos: possuiInvestimentos ? investimentos.filter((i) => i.item.trim()) : [],
      ...(!editando && isSuperAdmin ? { empresa_id: filtroEmpresa } : {}),
    };
    const res = editando
      ? await projetotService.update(editando.id, payload)
      : modeloOrigemId
        ? await projetotService.criarAPartir(modeloOrigemId, payload)
        : await projetotService.create(payload);
    if (res.success) {
      toast.success(editando ? 'Projeto atualizado' : 'Projeto criado');
      setModalOpen(false);
      if (!editando && modeloOrigemId) {
        // Navega direto pro projeto novo — a estrutura copiada do modelo vale a pena ver na hora.
        navigate(`/projetos/${res.data.id}`);
      } else {
        load();
      }
    } else {
      toast.error(res.error || 'Erro ao salvar projeto');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!paraExcluir) return;
    const res = await projetotService.delete(paraExcluir.id);
    if (res.success) {
      toast.success('Projeto excluído');
      setParaExcluir(null);
      load();
    } else {
      toast.error(res.error || 'Erro ao excluir projeto');
    }
  };

  const openTipoModal = (tipo?: TipoProjeto) => {
    setTipoEditando(tipo || null);
    setTipoNome(tipo?.nome || '');
    setTipoDescricao(tipo?.descricao || '');
    setTipoModalOpen(true);
  };

  const handleSaveTipo = async () => {
    if (!tipoNome.trim()) return;
    setTipoLoading(true);

    const empresa_id = isSuperAdmin
      ? (filtroEmpresa ?? undefined)
      : empresaAtiva?.id;

    if (isSuperAdmin && !empresa_id) {
      setTipoLoading(false);
      toast.error('Selecione uma empresa antes de criar um tipo de projeto');
      return;
    }

    const res = tipoEditando
      ? await tipoProjetoService.update(tipoEditando.id, { nome: tipoNome, descricao: tipoDescricao })
      : await tipoProjetoService.create({ nome: tipoNome, descricao: tipoDescricao, empresa_id: empresa_id! });
    setTipoLoading(false);
    if (res.success) {
      toast.success(tipoEditando ? 'Tipo atualizado' : 'Tipo criado');
      setTipoModalOpen(false);
      if (!tipoEditando && novoTipoParaForm) {
        setForm(f => ({ ...f, tipo_id: res.data.id }));
        setNovoTipoParaForm(false);
      }
      load();
    } else {
      toast.error(res.error || 'Erro ao salvar tipo');
    }
  };

  const handleDeleteTipo = async () => {
    if (!tipoDeletando) return;
    setTipoLoading(true);
    const res = await tipoProjetoService.delete(tipoDeletando.id);
    setTipoLoading(false);
    if (res.success) {
      toast.success('Tipo excluído');
      setTipoDeleteOpen(false);
      setTipoDeletando(null);
      load();
    } else {
      toast.error(res.error || 'Erro ao excluir tipo');
    }
  };

  const areasDisponiveis = Array.from(new Set(projetos.map(p => p.area).filter(Boolean))).sort();

  const filtrados = projetos
    .filter(p => filtroStatus === 'todos' || p.status_projeto === filtroStatus)
    .filter(p => filtroArea === 'todas' || p.area === filtroArea)
    .filter(p => filtroTipo === 'todos' || p.tipo_id === filtroTipo)
    .filter(p => !buscaTitulo.trim() || p.titulo.toLowerCase().includes(buscaTitulo.trim().toLowerCase()));

  const handleKanbanDragEnd = async (event: DragEndEvent) => {
    setKanbanDragId(null);
    const { active, over } = event;
    if (!over) return;
    const projetoId = active.id as string;
    const novoStatus = over.id as ProjetoStatus;
    const projeto = projetos.find(p => p.id === projetoId);
    if (!projeto || projeto.status_projeto === novoStatus) return;

    const anterior = projeto.status_projeto;
    setProjetos(prev => prev.map(p => p.id === projetoId ? { ...p, status_projeto: novoStatus } : p));
    const res = await projetotService.updateStatus(projetoId, novoStatus);
    if (!res.success) {
      toast.error(res.error || 'Transição de status inválida');
      setProjetos(prev => prev.map(p => p.id === projetoId ? { ...p, status_projeto: anterior } : p));
    } else {
      toast.success('Status atualizado');
    }
  };

  const statCards: { label: string; value: number; color: string; status: ProjetoStatus | 'todos' }[] = [
    { label: 'Total', value: dashboard?.totais.total ?? 0, color: 'text-foreground', status: 'todos' },
    { label: 'Em Edição', value: dashboard?.totais.em_edicao ?? 0, color: 'text-gray-500', status: 'em_edicao' },
    { label: 'Ag. Aprovação', value: dashboard?.totais.ag_aprovacao ?? 0, color: 'text-amber-500', status: 'ag_aprovacao' },
    { label: 'Em Andamento', value: dashboard?.totais.em_andamento ?? 0, color: 'text-blue-500', status: 'em_andamento' },
    { label: 'Ag. Validação', value: dashboard?.totais.ag_validacao ?? 0, color: 'text-violet-500', status: 'ag_validacao' },
    { label: 'Finalizado', value: dashboard?.totais.finalizado ?? 0, color: 'text-emerald-500', status: 'finalizado' },
    { label: 'Reprovado', value: dashboard?.totais.reprovado ?? 0, color: 'text-red-500', status: 'reprovado' },
  ];

  const pieData = (Object.keys(STATUS_LABELS) as ProjetoStatus[]).map(s => ({
    name: STATUS_LABELS[s],
    value: dashboard?.totais[s] ?? 0,
  }));

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Projetos</h1>
            <p className="text-sm text-muted-foreground">
              Projetos com fluxo de aprovação, seções e atividades.
            </p>
          </div>
          <Button className="gap-2 shrink-0" onClick={openCreate} disabled={isSuperAdmin && filtroEmpresa === null} data-tour="novo-projeto">
            <Plus className="h-4 w-4" />
            Novo Projeto
          </Button>
        </div>

        {isSuperAdmin && filtroEmpresa === null ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center">
            <GitBranch className="mb-3 h-10 w-10 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Selecione uma empresa</h2>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Projetos são por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
            </p>
          </div>
        ) : (
        <>
        {/* Métricas: ficam acima das abas, visíveis em Dashboard e em Projetos. Clicar numa
            métrica leva pra aba Projetos já com o filtro de status aplicado. */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {statCards.map(card => (
              <Card
                key={card.label}
                className="card-interactive cursor-pointer"
                onClick={() => irParaFiltro(card.status)}
              >
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs text-muted-foreground font-medium">{card.label}</CardTitle>
                </CardHeader>
                <CardContent className="pb-4 px-4">
                  <span className={`text-3xl font-bold ${card.color}`}>{card.value}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs value={aba} onValueChange={v => setAba(v as 'dashboard' | 'projetos')}>
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="projetos">Projetos</TabsTrigger>
            <TabsTrigger value="dashboard" className="gap-1.5">
              <GitBranch className="h-4 w-4" />Dashboard
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="configuracoes" className="gap-1.5">
                <Settings className="h-4 w-4" />Configurações
              </TabsTrigger>
            )}
          </TabsList>

          {/* ── Dashboard ─────────────────────────────────────────────────────── */}
          <TabsContent value="dashboard" className="space-y-6 mt-4">
            {loading ? null : (
              <>
                {/* Progresso médio: métrica que vem do lado Cronograma (atividades finalizadas) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Progresso médio das atividades</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <span className="text-3xl font-bold">{dashboard?.progresso_medio ?? 0}%</span>
                      <Progress value={dashboard?.progresso_medio ?? 0} className={cn("h-2", getProgressIndicatorStyle(dashboard?.progresso_medio ?? 0))} />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Total de atividades</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <span className="text-3xl font-bold">{dashboard?.total_atividades ?? 0}</span>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader><CardTitle className="text-sm font-medium">Por Tipo</CardTitle></CardHeader>
                    <CardContent>
                      {dashboard && dashboard.por_tipo.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={dashboard.por_tipo} layout="vertical">
                            <XAxis type="number" tick={{ fontSize: 12 }} />
                            <YAxis dataKey="tipo" type="category" tick={{ fontSize: 11 }} width={80} />
                            <Tooltip />
                            <Bar dataKey="total" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">Sem dados</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle className="text-sm font-medium">Por Status</CardTitle></CardHeader>
                    <CardContent>
                      {pieData.some(d => d.value > 0) ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={pieData.filter(d => d.value > 0)}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={({ percent }) => `${Math.round((percent ?? 0) * 100)}%`}
                            >
                              {pieData.filter(d => d.value > 0).map((_, index) => (
                                <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">Sem dados</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle className="text-sm font-medium">Por Mês</CardTitle></CardHeader>
                    <CardContent>
                      {dashboard && dashboard.por_mes.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={dashboard.por_mes}>
                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Bar dataKey="total" fill="#10b981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">Sem dados</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* ── Lista ─────────────────────────────────────────────────────────── */}
          <TabsContent value="projetos" className="space-y-4 mt-4">
            <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3">
              <Input
                placeholder="Buscar por título..."
                value={buscaTitulo}
                onChange={e => setBuscaTitulo(e.target.value)}
                className="w-56"
              />
              <Select value={filtroStatus} onValueChange={v => setFiltroStatus(v as ProjetoStatus | 'todos')}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  {(Object.keys(STATUS_LABELS) as ProjetoStatus[]).map(s => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroArea} onValueChange={setFiltroArea}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Filtrar por área" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as áreas</SelectItem>
                  {areasDisponiveis.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Filtrar por tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  {tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              {(buscaTitulo || filtroStatus !== 'todos' || filtroArea !== 'todas' || filtroTipo !== 'todos') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setBuscaTitulo(''); setFiltroStatus('todos'); setFiltroArea('todas'); setFiltroTipo('todos'); }}
                >
                  Limpar filtros
                </Button>
              )}
              {vista === 'lista' && <BotaoColunas gerenciador={colGerenciador} className="h-10" />}
              <div className="ml-auto flex items-center gap-1 rounded-md bg-muted p-1">
                <Button
                  variant={vista === 'lista' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 gap-1.5 px-2"
                  onClick={() => setVista('lista')}
                >
                  <LayoutList className="h-3.5 w-3.5" /> Lista
                </Button>
                <Button
                  variant={vista === 'kanban' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 gap-1.5 px-2"
                  onClick={() => setVista('kanban')}
                >
                  <KanbanIcon className="h-3.5 w-3.5" /> Kanban
                </Button>
              </div>
            </div>

            {vista === 'kanban' ? (
              <DndContext
                sensors={kanbanSensors}
                collisionDetection={closestCenter}
                onDragStart={(e: DragStartEvent) => setKanbanDragId(e.active.id as string)}
                onDragEnd={handleKanbanDragEnd}
              >
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {KANBAN_COLUNAS.map(status => {
                    const itens = filtrados.filter(p => p.status_projeto === status);
                    return (
                      <KanbanColuna key={status} status={status}>
                        <div className="flex items-center justify-between px-1 pb-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {STATUS_LABELS[status]}
                          </span>
                          <span className="text-xs text-muted-foreground">{itens.length}</span>
                        </div>
                        {itens.map(p => (
                          <KanbanCard key={p.id} projeto={p} onOpen={() => navigate(`/projetos/${p.id}`)} />
                        ))}
                        {itens.length === 0 && (
                          <p className="px-1 py-4 text-center text-xs text-muted-foreground">Nenhum projeto</p>
                        )}
                      </KanbanColuna>
                    );
                  })}
                </div>
                <DragOverlay>
                  {kanbanDragId ? (() => {
                    const p = filtrados.find(x => x.id === kanbanDragId);
                    return p ? <KanbanCard projeto={p} onOpen={() => {}} /> : null;
                  })() : null}
                </DragOverlay>
              </DndContext>
            ) : (
            <Card>
              <CardContent className="p-0">
                <Table style={{ tableLayout: 'fixed' }}>
                  <TableHeader>
                    <TableRow>
                      {colGerenciador.visiveisOrdenadas.map(({ id: colId, width, label }) => (
                        <TableHead key={colId} style={{ width }} className="relative overflow-hidden">
                          {label}
                          <ColunaResizeHandle onResize={deltaX => colGerenciador.redimensionar(colId, deltaX)} />
                        </TableHead>
                      ))}
                      <TableHead className="w-28 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={colGerenciador.visiveisOrdenadas.length + 1} className="py-8"><Skeleton className="h-6 w-full" /></TableCell>
                      </TableRow>
                    ) : filtrados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={colGerenciador.visiveisOrdenadas.length + 1} className="text-center text-muted-foreground py-8">
                          Nenhum projeto encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtrados.map(p => (
                        <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/projetos/${p.id}`)}>
                          {colGerenciador.visiveisOrdenadas.map(({ id: colId, width }) => {
                            const { className, content } = celulaProjeto(colId, p);
                            return (
                              <TableCell key={colId} style={{ width, maxWidth: width }} className={className}>
                                {content}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-end gap-1">
                              {podeEditar(p) && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar" onClick={() => openEdit(p)}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" title="Excluir" onClick={() => setParaExcluir(p)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* ── Configurações: Tipos de Projeto ─────────────────────────────────── */}
          {isAdmin && (
            <TabsContent value="configuracoes" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Tipos de Projeto</h2>
                <Button onClick={() => openTipoModal()} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Novo Tipo
                </Button>
              </div>

              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrição</TableHead>
                      {isSuperAdmin && <TableHead>Empresa</TableHead>}
                      <TableHead className="w-24">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tipos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isSuperAdmin ? 4 : 3} className="text-center text-muted-foreground py-8">
                          Nenhum tipo cadastrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      tipos.map((tipo) => (
                        <TableRow key={tipo.id}>
                          <TableCell>{tipo.nome}</TableCell>
                          <TableCell className="text-muted-foreground">{tipo.descricao || '—'}</TableCell>
                          {isSuperAdmin && (
                            <TableCell>{empresas.find(e => e.id === tipo.empresa_id)?.nome || '—'}</TableCell>
                          )}
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openTipoModal(tipo)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => { setTipoDeletando(tipo); setTipoDeleteOpen(true); }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          )}
        </Tabs>
        </>
        )}
      </div>

      {/* ── Modal criar/editar ──────────────────────────────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Projeto' : 'Novo Projeto'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {editando && (!editando.descricao || !editando.lider_id || !editando.tipo_id) && (
              <p className="col-span-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                Este projeto está com dados incompletos (veio do antigo módulo Cronogramas). Preencha objetivo, líder e tipo para salvar.
              </p>
            )}
            {!editando && projetos.some(p => p.is_modelo) && (
              <div className="col-span-2 space-y-1.5">
                <LabelComAjuda
                  texto="Criar a partir de modelo"
                  ajuda="Opcional. Escolhendo um modelo, as seções e atividades dele (incluindo o checklist de subatividades de cada uma) são copiadas pro projeto novo — sem status, datas nem anexos."
                />
                <Select value={modeloOrigemId || '__nenhum__'} onValueChange={v => setModeloOrigemId(v === '__nenhum__' ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__nenhum__">Nenhum (do zero)</SelectItem>
                    {projetos.filter(p => p.is_modelo).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label>Título *</Label>
              <Input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Objetivo *</Label>
              <Textarea
                rows={3}
                placeholder="O que este projeto pretende alcançar"
                value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <LabelComAjuda
                texto="Tipo *"
                ajuda={`Categoria oficial do projeto, escolhida numa lista já cadastrada pela empresa. Obrigatório: todo projeto precisa de um tipo. Não achou o tipo certo? ${isAdmin ? "Use 'Novo tipo' na própria lista para cadastrar um." : 'Peça a um administrador para cadastrar.'}`}
              />
              <Select
                value={form.tipo_id}
                onValueChange={v => {
                  if (v === '__novo__') {
                    setNovoTipoParaForm(true);
                    openTipoModal();
                    return;
                  }
                  setForm(f => ({ ...f, tipo_id: v }));
                }}
              >
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {tipos.map(t => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
                  {isAdmin && (
                    <SelectItem value="__novo__" className="text-primary font-medium">
                      + Novo tipo
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <LabelComAjuda
                texto="Área"
                ajuda="Setor ou departamento responsável pelo projeto, digitado livremente. Campo opcional, serve só como anotação."
              />
              <Input value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} placeholder="Ex: Logística - Recebimento" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Líder *</Label>
              <Popover open={liderPopoverOpen} onOpenChange={setLiderPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <span className={form.lider_id ? '' : 'text-muted-foreground'}>
                      {form.lider_id ? usuarios.find(u => u.id === form.lider_id)?.nome : 'Selecione'}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar líder..." value={liderSearch} onValueChange={setLiderSearch} />
                    <CommandList>
                      <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
                      <CommandGroup>
                        {usuarios
                          .filter(u => u.nome.toLowerCase().includes(liderSearch.toLowerCase()))
                          .map(u => (
                            <CommandItem
                              key={u.id}
                              value={u.nome}
                              onSelect={() => {
                                setForm(f => ({ ...f, lider_id: u.id }));
                                setLiderPopoverOpen(false);
                                setLiderSearch('');
                              }}
                            >
                              <Check className={cn('mr-2 h-4 w-4', form.lider_id === u.id ? 'opacity-100' : 'opacity-0')} />
                              {u.nome}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label>Data de Início</Label>
              <Input type="date" className="w-full min-w-0" value={form.data_inicio} onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label>Data de Fim</Label>
              <Input type="date" className="w-full min-w-0" value={form.data_fim} onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))} />
            </div>
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="investimentos" className="border rounded-lg px-4">
              <AccordionTrigger className="font-medium">Investimentos</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="possuiInvestimentosT"
                      checked={possuiInvestimentos}
                      onCheckedChange={(v) => {
                        setPossuiInvestimentos(!!v);
                        if (!v) setInvestimentos([]);
                      }}
                    />
                    <Label htmlFor="possuiInvestimentosT">Possui investimentos</Label>
                  </div>

                  {possuiInvestimentos && (
                    <div className="space-y-2">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left pb-2">Item</th>
                            <th className="text-left pb-2 w-28">Valor (R$)</th>
                            <th className="text-left pb-2 w-16">Qtd</th>
                            <th className="w-8" />
                          </tr>
                        </thead>
                        <tbody>
                          {investimentos.map((inv, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="py-1 pr-2">
                                <Input
                                  value={inv.item}
                                  onChange={(e) => updateInvestimento(idx, 'item', e.target.value)}
                                  placeholder="Descrição do item"
                                  className="h-8"
                                />
                              </td>
                              <td className="py-1 pr-2">
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={inv.valor}
                                  onChange={(e) => updateInvestimento(idx, 'valor', parseFloat(e.target.value) || 0)}
                                  className="h-8"
                                />
                              </td>
                              <td className="py-1 pr-2">
                                <Input
                                  type="number"
                                  min={1}
                                  value={inv.quantidade}
                                  onChange={(e) => updateInvestimento(idx, 'quantidade', parseInt(e.target.value) || 1)}
                                  className="h-8"
                                />
                              </td>
                              <td className="py-1">
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeInvestimento(idx)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <Button variant="outline" size="sm" onClick={addInvestimento} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Adicionar item
                      </Button>

                      {investimentos.length > 0 && (
                        <div className="text-right font-medium">Total: {formatBRL(totalInvestimentos)}</div>
                      )}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {editando && isAdmin && (
            <div className="flex items-center gap-2 pt-3">
              <Checkbox
                id="is_modelo"
                checked={!!form.is_modelo}
                onCheckedChange={val => setForm(f => ({ ...f, is_modelo: Boolean(val) }))}
              />
              <Label htmlFor="is_modelo" className="cursor-pointer font-normal flex items-center gap-1.5">
                Marcar como modelo
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground" tabIndex={-1}>
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="start" className="max-w-64">
                    Projetos marcados como modelo aparecem na opção "Criar a partir de modelo" ao
                    criar um projeto novo, pra reaproveitar a estrutura de seções e atividades.
                  </TooltipContent>
                </UiTooltip>
              </Label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!paraExcluir} onOpenChange={open => !open && setParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{paraExcluir?.titulo}</strong> será excluído junto com suas seções, atividades,
              responsáveis e investimentos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Modal criar/editar Tipo ──────────────────────────────────────────── */}
      <Dialog
        open={tipoModalOpen}
        onOpenChange={(open) => {
          setTipoModalOpen(open);
          if (!open) setNovoTipoParaForm(false);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{tipoEditando ? 'Editar Tipo' : 'Novo Tipo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tipoNomeT">Nome *</Label>
              <Input
                id="tipoNomeT"
                value={tipoNome}
                onChange={(e) => setTipoNome(e.target.value)}
                placeholder="Ex: Melhoria de Processo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tipoDescricaoT">Descrição</Label>
              <Textarea
                id="tipoDescricaoT"
                rows={2}
                value={tipoDescricao}
                onChange={(e) => setTipoDescricao(e.target.value)}
                placeholder="Opcional — detalhe o que esse tipo de projeto abrange"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setTipoModalOpen(false); setNovoTipoParaForm(false); }}
              disabled={tipoLoading}
            >
              Cancelar
            </Button>
            <Button onClick={handleSaveTipo} disabled={tipoLoading || !tipoNome.trim()}>
              {tipoLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Excluir Tipo ─────────────────────────────────────────────────────── */}
      <Dialog open={tipoDeleteOpen} onOpenChange={setTipoDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Tipo</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o tipo "{tipoDeletando?.nome}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTipoDeleteOpen(false)} disabled={tipoLoading}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteTipo} disabled={tipoLoading}>
              {tipoLoading ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/projetos.png" titulo="Projetos" />
    </AppLayout>
  );
}
